import { Paper, Stack, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures } from '@apple/ui/data-table/features';

import { budgetQueryOptions } from '../queries/budget';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export interface PurchaseSummaryTableProps {
	singleStoreCustomerCode: string;
	fiscalYear: number;
	periodNumber: number;
	categoryId: number;
}

export function PurchaseSummaryTable({
	singleStoreCustomerCode,
	fiscalYear,
	periodNumber,
	categoryId,
}: PurchaseSummaryTableProps) {
	const { t } = useTranslation('store');

	const purchaseSummaryQuery = useQuery(
		budgetQueryOptions.productPurchaseDetail(
			singleStoreCustomerCode,
			fiscalYear,
			periodNumber,
			categoryId,
		),
	);

	const table = useTable({
		features,
		data: purchaseSummaryQuery.data ?? [],
		enableSorting: true,
		columnResizeMode: 'onChange',
		enableFilters: false,
		enableColumnFilters: false,
		manualFiltering: false,
		manualSorting: false,

		columns: [
			{
				accessorKey: 'partNumber',
				header: t('budgets.purchaseSummary.headers.partNumber'),
			},
			{
				accessorKey: 'description',
				header: t('budgets.purchaseSummary.headers.description'),
			},
			{
				accessorKey: 'quantity',
				header: t('budgets.purchaseSummary.headers.quantity'),
			},
			{
				accessorKey: 'formattedTotalCost',
				header: t('budgets.purchaseSummary.headers.cost'),
			},
		],
	});

	function renderEmpty() {
		return (
			<Stack align='center'>
				<Title order={3}>{t('common:pagination:empty')}</Title>
			</Stack>
		);
	}

	return (
		<Paper withBorder>
			<DataTable
				hidePageSize
				table={table}
				variant='apple-table'
				renderEmpty={renderEmpty}
				loading={purchaseSummaryQuery.isFetching || purchaseSummaryQuery.isLoading}
			/>
		</Paper>
	);
}
